import type {
  Connection,
  LoiMoiOauth,
  LoiMoiOauthDaTao,
  KenhChuaKetNoi,
  DongBoNgayInput,
  DongBoNgayResult,
  KetQuaGoKhoiApi,
  KetQuaThuHoi,
  OauthStartResponse,
  QuotaUsage,
  SyncLogEntry,
  SyncRun,
  SyncScheduleRow,
  SyncSummary,
} from '@qlk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, goiApi, type LoiApi } from '../../lib/api.js';
import { KHOA_MOC_SO_LIEU } from '../analytics/api.js';

const GOC = '/api/v1/sync';

export const KHOA = {
  ketNoi: ['sync', 'connections'] as const,
  chuaKetNoi: ['sync', 'unconnected'] as const,
  tomTat: ['sync', 'summary'] as const,
  quota: ['sync', 'quota'] as const,
  lichSu: ['sync', 'runs'] as const,
  lich: ['sync', 'schedule'] as const,
  nhatKy: ['sync', 'log'] as const,
  loiMoi: (channelId: string) => ['sync', 'loi-moi', channelId] as const,
  loiMoiLichSu: (channelId: string) => ['sync', 'loi-moi-lich-su', channelId] as const,
};

export function useKetNoi() {
  return useQuery<Connection[], LoiApi>({
    queryKey: KHOA.ketNoi,
    queryFn: () => api.get<Connection[]>(`${GOC}/ket-noi`),
  });
}

export function useKenhChuaKetNoi() {
  return useQuery<KenhChuaKetNoi[], LoiApi>({
    queryKey: KHOA.chuaKetNoi,
    queryFn: () => api.get<KenhChuaKetNoi[]>(`${GOC}/kenh-chua-ket-noi`),
  });
}

/** `bat = false` cho người không có quyền xem module 03 (màn Quản lý kênh dùng). */
export function useTomTatDongBo(bat = true) {
  return useQuery<SyncSummary, LoiApi>({
    queryKey: KHOA.tomTat,
    queryFn: () => api.get<SyncSummary>(`${GOC}/summary`),
    enabled: bat,
  });
}

export function useQuota() {
  return useQuery<QuotaUsage, LoiApi>({
    queryKey: KHOA.quota,
    queryFn: () => api.get<QuotaUsage>(`${GOC}/quota`),
  });
}

export function useLichSuDongBo() {
  return useQuery<SyncRun[], LoiApi>({
    queryKey: KHOA.lichSu,
    queryFn: () => api.get<SyncRun[]>(`${GOC}/lich-su`),
  });
}

export function useLichDongBo() {
  return useQuery<SyncScheduleRow[], LoiApi>({
    queryKey: KHOA.lich,
    queryFn: () => api.get<SyncScheduleRow[]>(`${GOC}/lich`),
  });
}

export function useNhatKyDongBo() {
  return useQuery<SyncLogEntry[], LoiApi>({
    queryKey: KHOA.nhatKy,
    queryFn: () => api.get<SyncLogEntry[]>(`${GOC}/nhat-ky`),
  });
}

/* ------------------------------- Mutation ------------------------------ */

function lamMoiTatCa(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['sync'] }),
    qc.invalidateQueries({ queryKey: ['channels'] }),
    /* Đồng bộ xong là trục thời gian dài ra: lấy lịch sử 2024 về thì ô chọn kỳ
       phải có 2024 ngay, không chờ hết `staleTime` 5 phút. */
    qc.invalidateQueries({ queryKey: KHOA_MOC_SO_LIEU }),
  ]);
}

/**
 * Bắt đầu kết nối: lấy URL đồng ý của Google rồi chuyển hướng cả trang.
 * Ở chế độ mock, URL trỏ thẳng về callback nên luồng vẫn chạy trọn vẹn.
 */
export function useBatDauKetNoi() {
  return useMutation<OauthStartResponse, LoiApi, string>({
    mutationFn: (channelId) =>
      api.get<OauthStartResponse>(`${GOC}/oauth/start?channelId=${encodeURIComponent(channelId)}`),
    onSuccess: (du) => {
      window.location.assign(du.url);
    },
  });
}

export function useThuHoiKetNoi() {
  const qc = useQueryClient();
  return useMutation<KetQuaThuHoi, LoiApi, string>({
    mutationFn: (channelId) =>
      goiApi<KetQuaThuHoi>(`${GOC}/ket-noi/${encodeURIComponent(channelId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

/**
 * Gỡ kênh khỏi danh sách Kết nối API: thu hồi token nếu có rồi tắt cờ theo dõi
 * qua API. Bật lại ở hồ sơ kênh.
 */
export function useGoKhoiDanhSachApi() {
  const qc = useQueryClient();
  return useMutation<KetQuaGoKhoiApi, LoiApi, string>({
    mutationFn: (channelId) =>
      api.post<KetQuaGoKhoiApi>(
        `${GOC}/ket-noi/${encodeURIComponent(channelId)}/go-khoi-danh-sach`,
      ),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useDongBoNgay() {
  const qc = useQueryClient();
  return useMutation<DongBoNgayResult, LoiApi, Partial<DongBoNgayInput> | undefined>({
    mutationFn: (input) => api.post<DongBoNgayResult>(`${GOC}/dong-bo-ngay`, input ?? {}),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

/* ------------------- Link mời kết nối YouTube ------------------- */

export function useLoiMoiKetNoi(channelId: string, bat = true) {
  return useQuery<LoiMoiOauth[], LoiApi>({
    queryKey: KHOA.loiMoi(channelId),
    queryFn: () =>
      api.get<LoiMoiOauth[]>(`${GOC}/loi-moi?channelId=${encodeURIComponent(channelId)}`),
    enabled: bat,
  });
}

/**
 * Tạo link mời. `url` **chỉ** có trong kết quả lượt này — không endpoint nào đọc
 * lại được, vì database chỉ giữ hash của token.
 */
export function useTaoLoiMoiKetNoi() {
  const qc = useQueryClient();
  return useMutation<LoiMoiOauthDaTao, LoiApi, string>({
    mutationFn: (channelId) => api.post<LoiMoiOauthDaTao>(`${GOC}/loi-moi`, { channelId }),
    onSuccess: async (kq) => {
      await qc.invalidateQueries({ queryKey: KHOA.loiMoi(kq.channelId) });
      await qc.invalidateQueries({ queryKey: KHOA.loiMoiLichSu(kq.channelId) });
    },
  });
}

export function useThuHoiLoiMoiKetNoi() {
  const qc = useQueryClient();
  return useMutation<LoiMoiOauth, LoiApi, string>({
    mutationFn: (id) => api.post<LoiMoiOauth>(`${GOC}/loi-moi/${encodeURIComponent(id)}/thu-hoi`),
    onSuccess: async (kq) => {
      await qc.invalidateQueries({ queryKey: KHOA.loiMoi(kq.channelId) });
      await qc.invalidateQueries({ queryKey: KHOA.loiMoiLichSu(kq.channelId) });
    },
  });
}

/** Lịch sử link mời: mọi trạng thái, để biết ai đã cấp quyền và lúc nào. */
export function useLichSuLoiMoiKetNoi(channelId: string, bat = true) {
  return useQuery<LoiMoiOauth[], LoiApi>({
    queryKey: KHOA.loiMoiLichSu(channelId),
    queryFn: () =>
      api.get<LoiMoiOauth[]>(`${GOC}/loi-moi/lich-su?channelId=${encodeURIComponent(channelId)}`),
    enabled: bat,
  });
}
